/* UPVANITY interaction semantics for the unpublished QA theme. */
(() => {
  const quantityLabelText =
    document.currentScript?.dataset.quantityLabel?.trim() || 'Select quantity';
  const zeroResultsLabelText =
    document.currentScript?.dataset.zeroResultsLabel?.trim() || 'No results found.';
  const clearAllLabelText =
    document.currentScript?.dataset.clearAllLabel?.trim() || 'Clear all';

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /*
   * Pointer focus is intentionally limited to controls whose theme/plugin
   * focus treatment produces a second, oversized black rectangle. Everything
   * else keeps the browser/theme :focus-visible behavior.
   */
  const pointerFocusSelector = [
    '.open-quick-view',
    '.quickView',
    '.flickity-prev-next-button',
    '.slider-button',
    '.uv-header-search-trigger',
    '.gsw-prg-item--link',
    '#view-catalog a',
    '#catalog-view',
    '#setLimit',
    '.gf-filter-selection select',
    '.gf-filter-selection .sort-by-toggle',
    '#gfqv-modal .gfqv-close-modal',
    '#gfqv-modal .owl-prev',
    '#gfqv-modal .owl-next',
    '#gfqv-modal .quantity .decrease',
    '#gfqv-modal .quantity .increase',
    '#gfqv-modal .gfqv-swatch-value',
    '#gfqv-modal #gfqv-btn',
    '#yv_quickView_product .yv_side_drawer_close',
    '#yv_quickView_product [data-drawer-close]',
    '.pickup-availability-drawer .yv_side_drawer_close',
    '#ShowPickupAvailabilityDrawer',
    '#gf-tree .gf-close-canvas',
    '#glFilter-search-popup .gl-d-close-btn',
    '#mobileMenu .close-btn',
    '#mini__cart .yv_side_drawer_close',
    '#mini__cart .close-cart-drawer',
    '.quick-add-popup .popup-close',
    '.single__product .productOption',
    '.single__product .product-loop-variants .option',
    '.single__product .yv-product-quantity button',
    '.single__product .yv-product-quantity [role="button"]',
    '.single__product .Sd_addProduct',
    '.single__product .shopify-payment-button__button'
  ].join(',');

  const topPicksSectionSelector = [
    '[id$="__3666ad5c-9a57-4da8-85e3-51e692e12b68"]',
    '[id$="__tabbed_collections_7zrqKT"]'
  ].join(',');

  const productRailSectionSelector = [
    '[id$="__3666ad5c-9a57-4da8-85e3-51e692e12b68"]',
    '[id$="__tabbed_collections_eMC7Qy"]',
    '[id$="__tabbed_collections_7zrqKT"]',
    '[id$="__tabbed_collections_RR9btz"]',
    '[id$="__tabbed_collections_7GiF33"]'
  ].join(',');

  const runtimeRootSelector = [
    '#gf-tree',
    '#glFilter-search-popup',
    '#gfqv-modal',
    '#yv_quickView_product',
    '#mini__cart',
    '[data-side-drawer]',
    '#mobileMenu',
    '#view-catalog'
  ].join(',');

  let menuTrigger = null;
  let searchTrigger = null;
  let filterTrigger = null;
  let quickViewTrigger = null;
  let pickupTrigger = null;
  let cartTrigger = null;
  let cartTriggerGeneration = 0;
  const captureCartTrigger = (target) => {
    if (!target) return null;
    const ownership = {
      generation: ++cartTriggerGeneration,
      target
    };
    cartTrigger = ownership;
    return ownership;
  };
  const releaseCartTrigger = (ownership) => {
    if (!ownership || cartTrigger !== ownership) return false;
    cartTrigger = null;
    return true;
  };
  const consumeCartTrigger = () => {
    const returnTarget = cartTrigger?.target || null;
    cartTrigger = null;
    return returnTarget;
  };
  window.UpvanityCartFocusOwner = {
    version: 1,
    capture: captureCartTrigger,
    release: releaseCartTrigger
  };
  let menuElement = null;
  let menuFocusGeneration = 0;
  let menuWasOpen = false;
  let searchWasOpen = false;
  let catalogWasOpen = false;
  let filterWasOpen = false;
  let quickViewWasOpen = false;
  let cartWasOpen = false;
  let pickupWasOpen = false;
  let pickupFocusGeneration = 0;
  let pickupOwnership = null;
  let overlaySyncQueued = false;
  let merchandisingSyncQueued = false;
  let productRailResizeObserver = null;
  let runtimeStateObserver = null;
  let merchandisingObserver = null;
  let bodyStateObserver = null;
  let documentStateObserver = null;
  let runtimeDiscoveryObserver = null;
  let pointerFocusedControl = null;
  let pointerFocusedRail = null;
  const observedRuntimeRoots = new WeakSet();
  const observedMerchandisingSections = new WeakSet();
  const pendingMerchandisingRoots = new Set();

  document.documentElement.dataset.uvFocusModality = 'keyboard';

  const setFocusModality = (modality, control = null) => {
    pointerFocusedControl?.removeAttribute('data-uv-pointer-focus');
    pointerFocusedControl = modality === 'pointer' ? control : null;

    if (modality !== 'pointer') {
      pointerFocusedRail?.removeAttribute('data-uv-pointer-rail-focus');
      pointerFocusedRail = null;
    }

    document.documentElement.dataset.uvFocusModality = modality;

    if (pointerFocusedControl) {
      pointerFocusedControl.setAttribute('data-uv-pointer-focus', 'true');
    }
  };

  const pointerFocusControl = (target) => target?.closest?.(pointerFocusSelector) || null;

  const pointerFocusRail = (target) =>
    target?.closest?.('.yv-collection-product-grid.product-grid-carousel') || null;

  const setPointerFocusedRail = (target) => {
    pointerFocusedRail?.removeAttribute('data-uv-pointer-rail-focus');
    pointerFocusedRail = pointerFocusRail(target);
    pointerFocusedRail?.setAttribute('data-uv-pointer-rail-focus', 'true');
  };

  const focusWithoutScroll = (element) => {
    if (!element?.focus) return;
    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  };

  const focusForCurrentModality = (element) => {
    if (
      document.documentElement.dataset.uvFocusModality === 'pointer' &&
      element
    ) {
      setFocusModality('pointer', pointerFocusControl(element));
    }
    focusWithoutScroll(element);
  };

  const setAttribute = (element, name, value) => {
    if (element && element.getAttribute(name) !== String(value)) {
      element.setAttribute(name, String(value));
    }
  };

  const isVisible = (element) => {
    if (!element || !element.isConnected) return false;
    const style = window.getComputedStyle(element);
    return style.visibility !== 'hidden' && style.display !== 'none' && element.getClientRects().length > 0;
  };

  const isOnScreen = (element) => {
    if (!isVisible(element)) return false;
    const rect = element.getBoundingClientRect();
    return rect.right > 0 && rect.left < window.innerWidth && rect.bottom > 0 && rect.top < window.innerHeight;
  };

  const visibleFocusable = (root) =>
    [...root.querySelectorAll(focusableSelector)].filter((element) => isVisible(element));

  const addPaymentIcons = (dialog, anchor, metaSelector) => {
    if (!dialog || !anchor) return;
    const source = document.querySelector('footer .footer-payment .list-payment');
    if (!source) return;

    let wrapper = dialog.querySelector('.uv-quickview-payments');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'footer-payment uv-quickview-payments';
      wrapper.setAttribute('role', 'group');
      wrapper.setAttribute('aria-label', 'Accepted payment methods');

      const paymentList = source.cloneNode(true);
      paymentList.querySelectorAll('svg[aria-labelledby]').forEach((icon, index) => {
        const title = icon.querySelector('title[id]');
        if (!title) return;
        const titleId = `uv-${dialog.id || 'quickview'}-payment-${index}`;
        title.id = titleId;
        icon.setAttribute('aria-labelledby', titleId);
      });
      wrapper.append(paymentList);
    }

    if (anchor.nextElementSibling !== wrapper) {
      anchor.insertAdjacentElement('afterend', wrapper);
    }

    const meta = dialog.querySelector(metaSelector);
    if (meta && wrapper.nextElementSibling !== meta) {
      wrapper.insertAdjacentElement('afterend', meta);
    }
  };

  const trapTab = (event, root) => {
    const items = visibleFocusable(root);
    if (!items.length) {
      event.preventDefault();
      root.focus?.();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === root)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const filterIsOpen = () => {
    const dialog = document.getElementById('gf-tree');
    return Boolean(
      window.matchMedia('(max-width: 767px)').matches &&
      dialog &&
        dialog.getAttribute('aria-hidden') === 'false' &&
        !dialog.classList.contains('uv-force-closed')
    );
  };

  const globoQuickViewIsOpen = () => {
    const dialog = document.getElementById('gfqv-modal');
    return Boolean(dialog && document.body.classList.contains('globo-modal-open') && isVisible(dialog));
  };

  const nativeQuickViewIsOpen = () => {
    const dialog = document.getElementById('yv_quickView_product');
    return Boolean(
      dialog &&
        dialog.classList.contains('yv_quickView_product') &&
        document.body.classList.contains('side_Drawer_open') &&
        isOnScreen(dialog)
    );
  };

  const pickupDrawer = () =>
    document.querySelector('[data-side-drawer].pickup-availability-drawer');

  const currentPickupOwnership = (drawer = pickupDrawer()) => {
    const ownership = drawer ?
      window.UpvanityVariantLifecycle?.sharedDrawerOwners?.get?.(drawer) :
      null;
    return ownership?.owner === 'pickup' && !ownership.cancelled ? ownership : null;
  };

  const pickupDrawerIsOpen = () => {
    const drawer = pickupDrawer();
    return Boolean(
      drawer &&
        document.body.classList.contains('side_Drawer_open')
    );
  };

  const cartDrawerIsOpen = () => {
    const drawer = document.getElementById('mini__cart');
    if (!drawer || !drawer.classList.contains('mini_cart')) return false;
    if (document.body.classList.contains('minicart-open')) return isVisible(drawer);
    return document.body.classList.contains('side_Drawer_open') && isOnScreen(drawer);
  };

  const quickViewDialog = () => {
    if (globoQuickViewIsOpen()) return document.getElementById('gfqv-modal');
    if (nativeQuickViewIsOpen()) return document.getElementById('yv_quickView_product');
    return null;
  };

  const quickViewIsOpen = () => Boolean(quickViewDialog());

  const syncBackgroundInert = () => {
    const blockingOverlayOpen =
      document.body.classList.contains('nav-open') ||
      document.body.classList.contains('minicart-open') ||
      document.body.classList.contains('side_Drawer_open') ||
      quickViewIsOpen();
    const filterOpen = filterIsOpen();

    const main = document.getElementById('MainContent');
    const footer = document.querySelector('footer');
    if (main) main.inert = blockingOverlayOpen;
    if (footer) footer.inert = blockingOverlayOpen || filterOpen;
  };

  const syncFaq = (root = document) => {
    root.querySelectorAll('.faqSection-header[aria-controls]').forEach((control) => {
      const panel = document.getElementById(control.getAttribute('aria-controls'));
      const expanded = control.classList.contains('active');
      setAttribute(control, 'aria-expanded', expanded);
      if (panel) setAttribute(panel, 'aria-hidden', !expanded);
    });
  };

  const syncSubmenus = () => {
    document
      .querySelectorAll('#mobileMenu button.toggle[aria-controls], #mobileMenu button.toggle-level[aria-controls]')
      .forEach((control) => {
        const panel = document.getElementById(control.getAttribute('aria-controls'));
        const expanded = control.classList.contains('open-menu-drop') || panel?.classList.contains('is-open');
        setAttribute(control, 'aria-expanded', expanded);
        if (panel) setAttribute(panel, 'aria-hidden', !expanded);
      });
  };

  const catalogIsOpen = () => {
    const panel = document.getElementById('view-catalog');
    return Boolean(
      panel &&
        document.body.classList.contains('catalog-open') &&
        isVisible(panel)
    );
  };

  const enhanceCatalog = () => {
    const trigger = document.getElementById('catalog-view');
    const panel = document.getElementById('view-catalog');
    if (!trigger || !panel) return;

    setAttribute(trigger, 'role', 'button');
    setAttribute(trigger, 'tabindex', '0');
    setAttribute(trigger, 'aria-haspopup', 'true');
    setAttribute(trigger, 'aria-controls', panel.id);
    setAttribute(trigger, 'aria-expanded', catalogIsOpen());
    setAttribute(panel, 'aria-hidden', !catalogIsOpen());
  };

  const closeCatalog = (restoreFocus = false) => {
    const trigger = document.getElementById('catalog-view');
    const panel = document.getElementById('view-catalog');
    if (trigger && catalogIsOpen()) {
      trigger.click();
    } else {
      document.body.classList.remove('catalog-open');
      panel?.style.removeProperty('display');
    }
    catalogWasOpen = false;
    enhanceCatalog();
    if (restoreFocus && trigger) trigger.focus();
  };

  const syncCatalog = () => {
    enhanceCatalog();
    catalogWasOpen = catalogIsOpen();
  };

  const searchIsOpen = () => {
    const layer = document.getElementById('glFilter-search-popup');
    return Boolean(
      document.body.classList.contains('gl-search-open') ||
        layer?.getAttribute('aria-hidden') === 'false'
    );
  };

  const enhanceHeaderSearch = () => {
    const form = document.querySelector('.header_4 .yv-header-searchbar');
    const input = form?.querySelector('#search-drawer-input-query');
    const trigger = form?.querySelector(
      '.yv-header-searchbar-icon:not(button):not(.cross)'
    );
    if (!form || !input || !trigger) return;

    /*
     * Globo opens its modal on input focus. Keeping that field in the normal
     * Tab order therefore turns a routine Tab into an unexpected modal loop.
     * The adjacent search icon becomes the explicit, reversible trigger;
     * pointer users may still click the field itself.
     */
    input.tabIndex = -1;
    trigger.classList.add('uv-header-search-trigger');
    setAttribute(trigger, 'role', 'button');
    setAttribute(trigger, 'tabindex', '0');
    setAttribute(trigger, 'aria-label', 'Open search');
    setAttribute(trigger, 'aria-haspopup', 'dialog');
    setAttribute(trigger, 'aria-controls', 'glFilter-search-popup');
    setAttribute(trigger, 'aria-expanded', searchIsOpen());

    if (trigger.dataset.uvSearchReady === 'true') return;

    const activate = (event) => {
      event?.preventDefault();
      searchTrigger = trigger;
      focusWithoutScroll(input);
    };

    trigger.addEventListener('click', activate);
    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') activate(event);
    });
    trigger.dataset.uvSearchReady = 'true';
  };

  const captureSearchTrigger = (searchButton, headerSearchInput) => {
    if (headerSearchInput) {
      searchTrigger = document.querySelector('.uv-header-search-trigger');
    } else if (searchButton) {
      searchTrigger = searchButton;
    }
  };

  const syncSearchLayers = () => {
    enhanceHeaderSearch();
    const nativeSearch = document.querySelector('.search-bar-container');
    const globoLayer = document.getElementById('glFilter-search-popup');
    const initialContent = globoLayer?.querySelector('.gl-fullscreen-initial-content');
    if (initialContent) {
      const hasMeaningfulContent = Boolean(
        initialContent.textContent.trim() ||
          initialContent.querySelector('a, button, img, picture, svg, li')
      );
      initialContent.classList.toggle('uv-search-truly-empty', !hasMeaningfulContent);
    }
    const globoSearchOpen = searchIsOpen();
    const headerTrigger = document.querySelector('.uv-header-search-trigger');
    if (headerTrigger) setAttribute(headerTrigger, 'aria-expanded', globoSearchOpen);
    if (globoSearchOpen) {
      document.body.classList.remove('addsearch');
      if (nativeSearch) setAttribute(nativeSearch, 'aria-hidden', 'true');
    } else {
      nativeSearch?.removeAttribute('aria-hidden');
    }

    if (!globoSearchOpen && searchWasOpen && searchTrigger?.isConnected) {
      const triggerToRestore = searchTrigger;
      window.setTimeout(() => {
        if (!searchIsOpen()) focusForCurrentModality(triggerToRestore);
      }, 0);
    }
    searchWasOpen = globoSearchOpen;
  };

  const syncMenu = () => {
    const menu = document.getElementById('mobileMenu');
    if (!menu) return;

    const open = document.body.classList.contains('nav-open');
    const menuChanged = menuElement !== menu;
    if (menuChanged || open !== menuWasOpen) menuFocusGeneration += 1;
    const focusGeneration = menuFocusGeneration;

    /*
     * aria-hidden only changes the accessibility tree; it does not remove the
     * off-canvas links and buttons from sequential focus navigation. Release
     * inert before opening, then restore focus outside before making the
     * closed drawer inert so focus never remains inside an unavailable tree.
     */
    /* UPVANITY_MOBILE_MENU_INERT_GUARD_START */
    if (open) menu.inert = false;

    document.querySelectorAll('.navbar-toggler').forEach((trigger) => {
      setAttribute(trigger, 'aria-expanded', open);
    });

    if (!open && menuWasOpen && menuTrigger?.isConnected) {
      focusWithoutScroll(menuTrigger);
    }

    setAttribute(menu, 'aria-hidden', !open);

    if (open && (!menuWasOpen || menuChanged)) {
      window.setTimeout(() => {
        if (
          focusGeneration !== menuFocusGeneration ||
          menu !== menuElement ||
          !menu.isConnected ||
          !document.body.classList.contains('nav-open') ||
          menu.inert
        ) return;
        focusWithoutScroll(menu.querySelector('.close-btn'));
      }, 0);
    }

    if (!open) menu.inert = true;
    /* UPVANITY_MOBILE_MENU_INERT_GUARD_END */

    menuElement = menu;
    menuWasOpen = open;
    syncSubmenus();
    syncBackgroundInert();
  };

  const globoFilterRecoveryStates = new WeakMap();

  const syncGloboFilterRecovery = () => {
    if (!document.body.classList.contains('template_collection')) return;
    const anchor = document.querySelector('[data-upv-filter-recovery]');
    if (!anchor) return;
    const classes = document.documentElement.classList;
    const tree = document.getElementById('gf-tree');
    const controls = document.getElementById('gf-controls-container');
    const grid = document.getElementById('gf-grid');
    const products = document.getElementById('gf-products');
    const healthy = classes.contains('spf-has-filter') &&
      classes.contains('spf-status-loaded') && tree && controls && grid && products &&
      anchor.contains(grid) && grid.contains(products) && grid.contains(controls);
    let notice = anchor.querySelector('[data-upv-filter-recovery-notice]');
    if (healthy) {
      globoFilterRecoveryStates.set(anchor, { tree, controls, grid, products });
      if (notice) {
        const restoreFocus = notice.contains(document.activeElement);
        notice.remove();
        if (restoreFocus) {
          focusWithoutScroll(visibleFocusable(controls)[0]);
        }
      }
      return;
    }
    const previous = globoFilterRecoveryStates.get(anchor);
    if (!previous) return;
    const owningClasses = [
      'spf-has-filter', 'spf-status-loaded', 'spf-filter-loading',
      'gf-left', 'gf-top_one', 'gf-offcanvas', 'spf-layout-app', 'spf-layout-theme'
    ];
    const tornDown = !tree && !controls && !grid && !products &&
      !owningClasses.some((name) => classes.contains(name)) &&
      !previous.tree.isConnected && !previous.controls.isConnected &&
      previous.grid.isConnected && previous.products.isConnected &&
      previous.grid.id !== 'gf-grid' && previous.products.id !== 'gf-products' &&
      anchor.contains(previous.grid) && previous.grid.contains(previous.products);
    if (!tornDown) return;
    const message = anchor.dataset.recoveryMessage?.trim();
    const detail = anchor.dataset.recoveryDetail?.trim();
    const reload = anchor.dataset.recoveryReload?.trim();
    if (!message || !detail || !reload) return;
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'uv-globo-filter-recovery';
      notice.setAttribute('data-upv-filter-recovery-notice', 'true');
      const copy = document.createElement('p');
      copy.className = 'uv-globo-filter-recovery-copy';
      copy.setAttribute('role', 'status');
      copy.append(document.createElement('strong'), document.createElement('span'));
      const link = document.createElement('a');
      link.className = 'uv-globo-filter-recovery-reload';
      link.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 ||
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        window.location.reload();
      });
      notice.append(copy, link);
      anchor.prepend(notice);
    }
    const heading = notice.querySelector('strong');
    const description = notice.querySelector('span');
    const link = notice.querySelector('a');
    if (heading.textContent !== message) heading.textContent = message;
    if (description.textContent !== detail) description.textContent = detail;
    if (link.textContent !== reload) link.textContent = reload;
    if (link.getAttribute('href') !== window.location.href) {
      link.setAttribute('href', window.location.href);
    }
  };

  const collectionSearchStates = new WeakMap();

  const collectionSearchState = (input) => {
    let state = collectionSearchStates.get(input);
    if (!state) {
      state = { dirty: false, composing: false, draft: input.value, navigationPending: false };
      collectionSearchStates.set(input, state);
    }
    return state;
  };

  const syncGloboCollectionSearch = () => {
    if (!document.body.classList.contains('template_collection')) return;
    const input = document.querySelector('#gf-controls-container #gf-controls-search-input');
    if (!input) return;
    const state = collectionSearchState(input);
    const term = new URL(window.location.href).searchParams.get('q') || '';
    if (state.composing) return;
    if (state.navigationPending) {
      state.dirty = false;
      state.navigationPending = false;
    }
    if (state.dirty && state.draft === term) state.dirty = false;
    if (state.dirty) return;
    if (input.value !== term) input.value = term;
    state.draft = term;
  };

  const initGloboCollectionSearch = () => {
    if (!document.body.classList.contains('template_collection')) return;
    const trackInput = (event) => {
      const input = event.target;
      if (!input.matches?.('#gf-controls-container #gf-controls-search-input')) return;
      const state = collectionSearchState(input);
      if (event.type === 'input' && !state.composing && input.value !== state.draft) {
        state.navigationPending = false;
      }
      state.composing = event.type === 'compositionstart' ||
        (event.type !== 'compositionend' && (event.isComposing || state.composing));
      state.dirty = true;
      state.draft = input.value;
      if (event.type === 'compositionend') queueOverlaySync();
    };
    ['input', 'compositionstart', 'compositionend'].forEach((type) => {
      document.addEventListener(type, trackInput);
    });
    const restoreNavigation = () => {
      const input = document.querySelector('#gf-controls-container #gf-controls-search-input');
      if (input) {
        const state = collectionSearchState(input);
        state.navigationPending = state.composing;
        if (!state.composing) state.dirty = false;
      }
      queueOverlaySync();
    };
    window.addEventListener('popstate', restoreNavigation);
    window.addEventListener('pageshow', restoreNavigation);
    window.addEventListener('globoFilterRenderCompleted', queueOverlaySync);
  };

  const zeroResultRecovery = () => {
    const url = new URL(window.location.href);
    const hasSearch = Boolean(url.searchParams.get('q'));
    const hasFilters = [...url.searchParams.keys()].some((key) => key.startsWith('gf_'));
    const appSearchClear = document.querySelector(
      '#gf-controls-container .gf-controls-clear-button, #gf-grid .gf-controls-clear-button, #gf-tree .gf-controls-clear-button'
    );
    const appSearchClearLabel =
      appSearchClear?.getAttribute('aria-label')?.trim() ||
      appSearchClear?.textContent?.trim();

    if (hasSearch) {
      url.searchParams.delete('q');
      if (url.searchParams.get('sort_by') === 'relevance') {
        url.searchParams.delete('sort_by');
      }
    } else {
      url.searchParams.delete('q');
      [...url.searchParams.keys()].forEach((key) => {
        if (key.startsWith('gf_')) url.searchParams.delete(key);
      });
    }

    url.searchParams.delete('page');
    url.hash = '';
    return {
      href: url.href,
      label: hasSearch ? appSearchClearLabel || '' : hasFilters ? clearAllLabelText : ''
    };
  };

  const syncGloboZeroSelections = (shell) => {
    const url = new URL(window.location.href);
    const filter = window.GloboEmbedFilterConfig?.filter;
    const optionKeys = filter?.filterOptionKeys?.[filter.id];
    const clearText = window.GloboFilterTranslation?.filter?.clear;
    const clearLabel = typeof clearText === 'string' ? clearText.trim() : '';
    const keys = Array.isArray(optionKeys)
      ? optionKeys.map((key) => `${filter.prefix || 'gf_'}${key}`)
      : [];
    const groups = [...new Set(keys)].map((key) => ({
      key,
      values: url.searchParams.getAll(key).filter((value) => value !== '')
    })).filter((group) => group.values.length);
    let list = shell.querySelector('[data-upv-zero-selections]');
    if (!groups.length) {
      list?.remove();
      return;
    }
    if (!list) {
      list = document.createElement('ul');
      list.className = 'uv-globo-zero-filter-selections';
      list.setAttribute('data-upv-zero-selections', 'true');
      shell.prepend(list);
    }
    const remaining = new Set(groups.map((group) => group.key));
    [...list.children].forEach((item) => {
      if (!remaining.has(item.dataset.filterKey)) item.remove();
    });
    groups.forEach(({ key, values }) => {
      let item = [...list.children].find((node) => node.dataset.filterKey === key);
      if (!item) {
        item = document.createElement('li');
        item.dataset.filterKey = key;
        item.append(document.createElement('span'));
        list.append(item);
      }
      const value = values.join(' · ');
      const text = item.querySelector('span');
      if (text.textContent !== value) text.textContent = value;
      let link = item.querySelector('a');
      if (!clearLabel) {
        link?.remove();
        return;
      }
      if (!link) {
        link = document.createElement('a');
        item.append(link);
      }
      const target = new URL(url.href);
      target.searchParams.delete(key);
      target.searchParams.delete('page');
      target.hash = '';
      if (link.getAttribute('href') !== target.href) link.setAttribute('href', target.href);
      if (link.textContent !== clearLabel) link.textContent = clearLabel;
      const label = `${clearLabel}: ${value}`;
      if (link.getAttribute('aria-label') !== label) link.setAttribute('aria-label', label);
    });
  };

  const syncGloboCollectionZeroState = () => {
    if (!document.body.classList.contains('template_collection')) return;
    const tree = document.getElementById('gf-tree');
    const grid = document.getElementById('gf-grid');
    if (!tree || !grid) return;

    const empty = tree.classList.contains('spf-empty');
    const contents = tree.querySelector(':scope > .gf-filter-contents');

    if (!empty && contents?.children.length) {
      contents
        .querySelectorAll('[data-upv-zero-filter-shell]')
        .forEach((node) => node.remove());
      return;
    }

    if (!empty || !contents) return;
    let shell = contents.querySelector('[data-upv-zero-filter-shell]');
    if (!shell) {
      shell = document.createElement('div');
      shell.className = 'uv-globo-zero-filter-shell';
      shell.setAttribute('data-upv-zero-filter-shell', 'true');
      const status = document.createElement('p');
      status.className = 'uv-globo-zero-filter-status';
      status.setAttribute('role', 'status');
      status.textContent = zeroResultsLabelText;
      shell.append(status);
      contents.append(shell);
    }
    syncGloboZeroSelections(shell);
    const recovery = zeroResultRecovery();
    let recoveryLink = shell.querySelector('.uv-globo-zero-filter-recovery');
    if (!recovery.label) {
      recoveryLink?.remove();
      return;
    }
    if (!recoveryLink) {
      recoveryLink = document.createElement('a');
      recoveryLink.className = 'uv-globo-zero-filter-recovery';
      shell.append(recoveryLink);
    }
    if (recoveryLink.getAttribute('href') !== recovery.href) {
      recoveryLink.setAttribute('href', recovery.href);
    }
    if (recoveryLink.textContent !== recovery.label) recoveryLink.textContent = recovery.label;
  };

  const enhanceFilter = (dialog) => {
    if (!window.matchMedia('(max-width: 767px)').matches) {
      ['role', 'aria-modal', 'aria-hidden', 'aria-labelledby', 'aria-label'].forEach((name) => {
        if (dialog.hasAttribute(name)) dialog.removeAttribute(name);
      });
      dialog.classList.remove('uv-force-closed');
      return;
    }

    setAttribute(dialog, 'role', 'dialog');
    setAttribute(dialog, 'aria-modal', 'true');

    const heading = dialog.querySelector('.gf-filter-heading');
    if (heading) {
      if (!heading.id) heading.id = 'gf-filter-title';
      setAttribute(dialog, 'aria-labelledby', heading.id);
    } else if (!dialog.hasAttribute('aria-label')) {
      setAttribute(dialog, 'aria-label', 'Product filters');
    }

    const close = dialog.querySelector('.gf-close-canvas');
    if (close) {
      setAttribute(close, 'type', 'button');
      setAttribute(close, 'aria-label', 'Close filter');
    }
  };

  const syncFilter = () => {
    const dialog = document.getElementById('gf-tree');
    if (!dialog) {
      filterWasOpen = false;
      syncBackgroundInert();
      return;
    }

    enhanceFilter(dialog);
    const open = filterIsOpen();
    if (open && !filterWasOpen) {
      window.setTimeout(() => {
        if (filterIsOpen()) dialog.querySelector('.gf-close-canvas')?.focus();
      }, 60);
    } else if (!open && filterWasOpen && filterTrigger?.isConnected) {
      filterTrigger.focus();
    }

    filterWasOpen = open;
    syncBackgroundInert();
  };

  const forceFilterClosed = () => {
    const dialog = document.getElementById('gf-tree');
    if (!dialog) return;
    dialog.classList.add('uv-force-closed');
    setAttribute(dialog, 'aria-hidden', 'true');
    document.documentElement.classList.remove('offcanvas-open');
    queueOverlaySync();
  };

  const closeFilter = () => {
    const close = document.querySelector('#gf-tree .gf-close-canvas');
    close?.click();
    window.setTimeout(() => {
      const dialog = document.getElementById('gf-tree');
      if (dialog && dialog.getAttribute('aria-hidden') !== 'true') forceFilterClosed();
    }, 160);
  };

  const productHandleFromUrl = (url) => {
    const match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match?.[1] || '';
  };

  let productGroupCache = [];

  const parseEmbeddedJsonArray = (source, marker) => {
    const markerIndex = source.indexOf(marker);
    const startIndex = markerIndex < 0 ? -1 : source.indexOf('[', markerIndex + marker.length);
    if (startIndex < 0) return null;

    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let index = startIndex; index < source.length; index += 1) {
      const character = source[index];

      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (character === '\\') {
          escaped = true;
        } else if (character === '"') {
          inString = false;
        }
        continue;
      }

      if (character === '"') {
        inString = true;
      } else if (character === '[') {
        depth += 1;
      } else if (character === ']') {
        depth -= 1;
        if (depth === 0) {
          try {
            return JSON.parse(source.slice(startIndex, index + 1));
          } catch (_error) {
            return null;
          }
        }
      }
    }

    return null;
  };

  const readEmbeddedProductGroups = () => {
    const marker = 'var productGroupsGloboSwatchNewJs =';
    for (const script of document.scripts) {
      const source = script.textContent || '';
      if (!source.includes(marker)) continue;
      const embeddedGroups = parseEmbeddedJsonArray(source, marker);
      if (Array.isArray(embeddedGroups) && embeddedGroups.length) return embeddedGroups;
    }
    return [];
  };

  const snapshotProductGroups = (groups) => {
    if (!Array.isArray(groups)) return [];
    try {
      return typeof window.structuredClone === 'function'
        ? window.structuredClone(groups)
        : JSON.parse(JSON.stringify(groups));
    } catch (_error) {
      return groups.map((group) => ({
        ...group,
        products: Array.isArray(group?.products)
          ? group.products.map((product) => ({ ...product }))
          : []
      }));
    }
  };

  const getProductGroups = (forceEmbeddedRefresh = false) => {
    if (forceEmbeddedRefresh || !productGroupCache.length) {
      const embeddedGroups = readEmbeddedProductGroups();
      if (embeddedGroups.length) {
        productGroupCache = snapshotProductGroups(embeddedGroups);
        return productGroupCache;
      }
    }

    if (productGroupCache.length) return productGroupCache;

    const configuredGroups = window.GloboSwatchConfig?.product_groups;
    const liveGroups = window.productGroupsGloboSwatchNewJs;
    const availableGroups = [configuredGroups, liveGroups].find(
      (groups) => Array.isArray(groups) && groups.length
    );

    if (availableGroups) productGroupCache = snapshotProductGroups(availableGroups);

    return productGroupCache;
  };

  const findProductGroup = (handle, productId = '', context = 'product') => {
    if ((!handle && !productId)) return null;

    const findMatch = (groups) => Array.isArray(groups)
      ? groups.find((group) =>
        (context === 'collection'
          ? group?.show_collection !== false
          : group?.show_product !== false) &&
        Array.isArray(group.products) &&
        group.products.some((product) =>
          (productId && String(product?.id) === String(productId)) ||
          product?.handle === handle
        )
      )
      : null;

    const cachedMatch = findMatch(getProductGroups());
    if (cachedMatch) return cachedMatch;

    /* Globo mutates its live global; retry against the immutable inline source on a miss. */
    return findMatch(getProductGroups(true)) || null;
  };

  const applyProductGroupColor = (swatch, product) => {
    const first = product?.color1 || product?.title;
    const second = product?.color2;
    const firstIsColor = first && window.CSS?.supports?.('color', first);
    const secondIsColor = second && window.CSS?.supports?.('color', second);

    if (firstIsColor && secondIsColor) {
      swatch.style.background = `linear-gradient(135deg, ${first} 0 50%, ${second} 50% 100%)`;
    } else if (firstIsColor) {
      swatch.style.backgroundColor = first;
    }
  };

  const syncTopPicksSwatchOverflow = (field, productLink) => {
    if (!field?.isConnected) return;
    const list = field.querySelector('.gsw-list-products-group, .product-loop-variants');
    if (!list) return;

    const items = [...list.children].filter((item) =>
      item.matches('.gsw-prg-item, .color_option')
    );
    let overflowItem = list.querySelector(':scope > .uv-swatch-overflow');
    if (!overflowItem) {
      const listElement = list.matches('ul, ol');
      overflowItem = document.createElement(listElement ? 'li' : 'a');
      overflowItem.className = 'uv-swatch-overflow';
      const overflowLink = listElement ? document.createElement('a') : overflowItem;
      overflowLink.href = productLink?.getAttribute('href') || '#';
      overflowLink.textContent = '…';
      overflowLink.setAttribute('aria-label', 'View all color options on product page');
      overflowLink.title = 'View all color options';
      if (listElement) overflowItem.append(overflowLink);
      list.append(overflowItem);
    }

    const overflowLink = overflowItem.matches('a')
      ? overflowItem
      : overflowItem.querySelector('a');
    const productHref = productLink?.getAttribute('href');
    if (overflowLink && productHref && overflowLink.getAttribute('href') !== productHref) {
      overflowLink.setAttribute('href', productHref);
    }

    const setItemHidden = (item, hidden) => {
      item.classList.toggle('uv-swatch-is-overflow', hidden);
      if (hidden) {
        if (item.getAttribute('aria-hidden') !== 'true') item.setAttribute('aria-hidden', 'true');
      } else if (item.hasAttribute('aria-hidden')) {
        item.removeAttribute('aria-hidden');
      }
    };
    const revealAllItems = () => items.forEach((item) => setItemHidden(item, false));
    const mobile = window.matchMedia('(max-width: 767px)').matches;

    if (!mobile || items.length < 2) {
      revealAllItems();
      if (!overflowItem.hidden) overflowItem.hidden = true;
      return;
    }

    const listStyle = getComputedStyle(list);
    const gap = Number.parseFloat(listStyle.columnGap || listStyle.gap) || 0;
    const sampleWidth = items[0]?.getBoundingClientRect().width || 30;
    const availableWidth = list.clientWidth;
    const mobileSwatchLimit = 4;
    const fullCapacity = Math.max(
      1,
      Math.floor((availableWidth + gap) / (sampleWidth + gap))
    );
    const hasOverflow = items.length > Math.min(mobileSwatchLimit, fullCapacity);

    if (!hasOverflow) {
      revealAllItems();
      if (!overflowItem.hidden) overflowItem.hidden = true;
      return;
    }

    /* The ellipsis is not a swatch; its -3px lead margin keeps four 30px swatches at 375px. */
    const indicatorWidth = 9;
    const fittingCapacity = Math.max(
      1,
      Math.floor((availableWidth - indicatorWidth) / (sampleWidth + gap))
    );
    const visibleCapacity = Math.min(mobileSwatchLimit, fittingCapacity);
    const visibleIndexes = new Set(
      items.slice(0, visibleCapacity).map((item) => items.indexOf(item))
    );
    const activeIndex = items.findIndex((item) =>
      item.matches('.parent-is-gsw-active, :has(.is-gsw-active), :has(:checked)')
    );
    if (activeIndex >= visibleCapacity) {
      visibleIndexes.delete(visibleCapacity - 1);
      visibleIndexes.add(activeIndex);
    }

    items.forEach((item, index) => {
      const hidden = !visibleIndexes.has(index);
      setItemHidden(item, hidden);
    });
    if (overflowItem.hidden) overflowItem.hidden = false;
  };

  const enhanceTopPicksProductGroups = (root = document) => {
    const sections = new Set();
    if (root.matches?.(topPicksSectionSelector)) sections.add(root);
    root.querySelectorAll?.(topPicksSectionSelector).forEach((section) => sections.add(section));

    sections.forEach((section) => section.querySelectorAll('.yv-product-card').forEach((card) => {
      const productLink = card.querySelector('.yv-product-title[href*="/products/"]');
      const price = card.querySelector('.yv-prizebox');
      const handle = productHandleFromUrl(productLink?.getAttribute('href'));
      const productId = card.dataset.productId || '';
      const existing = card.querySelector('.uv-top-picks-gsw');
      const nativeColor = card.querySelector('.select-color.color-variants-wrapper');

      const group = findProductGroup(handle, productId, 'collection');
      const groupProducts = group?.products?.filter((product) => product?.handle) || [];
      const intendedSource = groupProducts.length >= 2 ? 'group' : nativeColor ? 'native' : '';
      if (
        existing?.dataset.productHandle === handle &&
        existing.dataset.swatchSource === intendedSource
      ) {
        syncTopPicksSwatchOverflow(existing, productLink);
        return;
      }
      existing?.remove();

      if (!price || (groupProducts.length < 2 && !nativeColor)) return;

      const field = document.createElement('div');
      field.className = 'uv-top-picks-gsw';
      field.dataset.productHandle = handle;
      if (groupProducts.length >= 2) {
        field.dataset.swatchSource = 'group';
        const detail = document.createElement('div');
        detail.className = 'globo-product-groups-detail';
        const groupsList = document.createElement('div');
        groupsList.className = 'gsw-list-product-groups';
        const groupItem = document.createElement('div');
        groupItem.className = 'gsw-item-product-group gsw-item-pg-style-product-img';
        const products = document.createElement('div');
        products.className = 'gsw-list-products-group';
        products.dataset.productgroupIndex = '0';

        groupProducts.forEach((product) => {
          const isCurrentProduct =
            (productId && String(product?.id) === String(productId)) ||
            product.handle === handle;
          const productHandle = isCurrentProduct ? handle : product.handle;
          const item = document.createElement('div');
          item.className = 'gsw-prg-item';
          const link = document.createElement('a');
          link.className = 'gsw-prg-item--link';
          link.href = `/products/${productHandle}`;
          link.setAttribute(
            'aria-label',
            `${group.option_name || 'Color'}: ${product.title || product.handle}`
          );
          link.dataset.title = product.title || '';
          if (product.image) link.dataset.image = product.image;

          if (isCurrentProduct) {
            item.classList.add('parent-is-gsw-active');
            link.classList.add('is-gsw-active');
            link.setAttribute('aria-current', 'true');
          }

          const swatch = document.createElement('span');
          swatch.className = [
            'swatch-anchor',
            'swatch-other',
            'globo-border-color-swatch',
            'globo-detail-style-square',
            'globo-detail-size-medium',
            'globo-detail-border-style-double',
            'globo-detail-hover-effects-none'
          ].join(' ');
          applyProductGroupColor(swatch, product);
          link.append(swatch);
          item.append(link);
          products.append(item);
        });

        groupItem.append(products);
        groupsList.append(groupItem);
        detail.append(groupsList);
        field.append(detail);
      } else {
        field.dataset.swatchSource = 'native';
        nativeColor.classList.add('uv-top-picks-native-source');
        nativeColor.removeAttribute('aria-hidden');
        field.append(nativeColor);
      }
      price.insertAdjacentElement('afterend', field);
      syncTopPicksSwatchOverflow(field, productLink);
    }));
  };

  const enhanceNativeProductGroup = (dialog) => {
    const productLink = dialog.querySelector('.yv-product-detail-title a[href*="/products/"]');
    const nativeColor = dialog.querySelector('.select-color.product-option-selector');
    const handle = productHandleFromUrl(productLink?.getAttribute('href'));
    const productFormReference = dialog
      .querySelector('[form^="product-form-"]')
      ?.getAttribute('form') || '';
    const productId = productFormReference.match(/product-form-(\d+)/)?.[1] || '';
    const existing = dialog.querySelector('.uv-native-gsw');

    if (existing?.dataset.productHandle === handle) return;
    existing?.remove();
    nativeColor?.classList.remove('uv-native-color-source');
    nativeColor?.removeAttribute('aria-hidden');

    const group = findProductGroup(handle, productId);
    if (!group || group.products.length < 2) return;

    const field = document.createElement('div');
    field.className = 'uv-native-gsw';
    field.dataset.productHandle = handle;

    const sourceLabel = nativeColor?.querySelector(':scope > .yv-variant-title');
    const label = sourceLabel?.cloneNode(true) || document.createElement('span');
    label.removeAttribute?.('for');
    label.classList.add('yv-variant-title');
    if (!label.textContent.trim()) label.textContent = group.option_name || 'Select color';
    field.append(label);

    const detail = document.createElement('div');
    detail.className = 'globo-product-groups-detail';
    const groupsList = document.createElement('div');
    groupsList.className = 'gsw-list-product-groups';
    const groupItem = document.createElement('div');
    groupItem.className = 'gsw-item-product-group gsw-item-pg-style-product-img';
    const products = document.createElement('div');
    products.className = 'gsw-list-products-group';
    products.dataset.productgroupIndex = '0';

    group.products.forEach((product) => {
      if (!product?.handle) return;
      const isCurrentProduct =
        (productId && String(product?.id) === String(productId)) ||
        product.handle === handle;
      const productHandle = isCurrentProduct ? handle : product.handle;
      const item = document.createElement('div');
      item.className = 'gsw-prg-item';
      const link = document.createElement('a');
      link.className = 'gsw-prg-item--link';
      link.href = `/products/${productHandle}`;
      link.setAttribute('aria-label', product.title || group.option_name || 'Color');
      link.dataset.title = product.title || '';
      if (product.image) link.dataset.image = product.image;

      if (isCurrentProduct) {
        item.classList.add('parent-is-gsw-active');
        link.classList.add('is-gsw-active');
        link.setAttribute('aria-current', 'page');
      }

      const swatch = document.createElement('span');
      swatch.className = [
        'swatch-anchor',
        'swatch-other',
        'globo-border-color-swatch',
        'globo-detail-style-square',
        'globo-detail-size-medium',
        'globo-detail-border-style-double',
        'globo-detail-hover-effects-none'
      ].join(' ');
      applyProductGroupColor(swatch, product);
      link.append(swatch);
      item.append(link);
      products.append(item);
    });

    groupItem.append(products);
    groupsList.append(groupItem);
    detail.append(groupsList);
    field.append(detail);
    const insertionAnchor = nativeColor || dialog.querySelector(
      '.select-size.product-option-selector, .yv-product-quantity, .product_button'
    );
    if (!insertionAnchor) return;
    insertionAnchor.insertAdjacentElement('beforebegin', field);
    if (nativeColor) {
      nativeColor.classList.add('uv-native-color-source');
      setAttribute(nativeColor, 'aria-hidden', 'true');
    }
  };

  const globoVariantSentinelStates = new WeakMap();

  const restoreGloboVariantSentinel = (value) => {
    if (!value?.classList.contains('uv-gfqv-variant-sentinel')) return;
    const previousState = globoVariantSentinelStates.get(value);
    value.classList.remove('uv-gfqv-variant-sentinel');
    if (!previousState) return;

    value.hidden = previousState.hidden;
    ['aria-hidden', 'tabindex'].forEach((name) => {
      const previousValue = previousState.attributes[name];
      if (previousValue === null) value.removeAttribute(name);
      else value.setAttribute(name, previousValue);
    });
    if (previousState.display.value) {
      value.style.setProperty(
        'display',
        previousState.display.value,
        previousState.display.priority
      );
    } else {
      value.style.removeProperty('display');
    }
    globoVariantSentinelStates.delete(value);
  };

  const concealGloboVariantSentinel = (value) => {
    if (!globoVariantSentinelStates.has(value)) {
      globoVariantSentinelStates.set(value, {
        hidden: value.hidden,
        attributes: {
          'aria-hidden': value.getAttribute('aria-hidden'),
          tabindex: value.getAttribute('tabindex')
        },
        display: {
          value: value.style.getPropertyValue('display'),
          priority: value.style.getPropertyPriority('display')
        }
      });
    }
    value.classList.add('uv-gfqv-variant-sentinel');
    value.hidden = true;
    value.setAttribute('aria-hidden', 'true');
    value.setAttribute('tabindex', '-1');
    value.style.setProperty('display', 'none', 'important');
  };

  /*
   * Globo's variant resolver reads the selected value from every native
   * .gfqv-swatch before it updates #gfqv-select, price, media and availability.
   * Keep those exact nodes in the option list even when the single current
   * colour is represented by the cross-product colour group. If the vendor
   * topology is not the known one-colour shape, leave its UI untouched.
   */
  const syncGloboProductColorGroup = ({ colorSwatch, productId, sourceGroup }) => {
    const colorValues = colorSwatch?.querySelector('.gfqv-swatch-values');
    if (!colorValues) return false;

    const existingGroup = colorValues.querySelector(':scope > .uv-gfqv-color-group');
    const nativeValues = [
      ...colorValues.querySelectorAll(':scope > .gfqv-swatch-value')
    ];
    const sourceLinks = sourceGroup?.querySelectorAll
      ? [...sourceGroup.querySelectorAll('.gsw-prg-item--link')]
      : [];
    const sourceSignatureParts = sourceLinks.map((link) => {
      const swatch = link.querySelector('.swatch-anchor');
      const href = String(link.getAttribute('href') || '');
      if (!href || !swatch) return '';
      return `${href}::${swatch.getAttribute('style') || ''}`;
    });
    const sourceSignature =
      sourceSignatureParts.length > 1 && sourceSignatureParts.every(Boolean)
        ? sourceSignatureParts.join('|')
        : '';
    const selectedNativeValue = nativeValues.find(
      (value) =>
        value.classList.contains('is-selected') &&
        Boolean(value.getAttribute('data-value'))
    );
    const canEnhance = Boolean(
      productId &&
      sourceSignature &&
      nativeValues.length === 1 &&
      selectedNativeValue
    );

    if (!canEnhance) {
      existingGroup?.remove();
      nativeValues.forEach(restoreGloboVariantSentinel);
      return false;
    }

    if (
      existingGroup?.dataset.productId === productId &&
      existingGroup.dataset.sourceSignature === sourceSignature
    ) {
      concealGloboVariantSentinel(selectedNativeValue);
      return true;
    }

    existingGroup?.remove();
    nativeValues.forEach(restoreGloboVariantSentinel);

    let clonedSourceGroup = null;
    try {
      clonedSourceGroup = sourceGroup.cloneNode(true);
    } catch (_error) {
      return false;
    }
    if (!clonedSourceGroup) return false;
    if (clonedSourceGroup.querySelector('.gfqv-swatch-value')) return false;

    clonedSourceGroup.querySelectorAll('legend').forEach((legend) => legend.remove());
    const colorGroup = document.createElement('li');
    colorGroup.classList.add('uv-gfqv-color-group');
    colorGroup.dataset.productId = productId;
    colorGroup.dataset.sourceSignature = sourceSignature;
    colorGroup.append(clonedSourceGroup);
    colorValues.append(colorGroup);
    concealGloboVariantSentinel(selectedNativeValue);
    return true;
  };

  const enhanceQuickView = (dialog) => {
    const isGlobo = dialog.id === 'gfqv-modal';
    setAttribute(dialog, 'role', 'dialog');
    setAttribute(dialog, 'aria-modal', 'true');
    setAttribute(dialog, 'aria-hidden', !(
      isGlobo ? globoQuickViewIsOpen() : nativeQuickViewIsOpen()
    ));
    if (!dialog.hasAttribute('tabindex')) setAttribute(dialog, 'tabindex', '-1');

    const heading = dialog.querySelector(
      '.gfqv-product-title, .gfqv-product-info h1, .gfqv-product-info h2, [data-drawer-title], .yv-product-detail-title'
    );
    if (heading) {
      if (!heading.id) heading.id = `${dialog.id || 'quickview'}-title`;
      setAttribute(dialog, 'aria-labelledby', heading.id);
      dialog.removeAttribute('aria-label');
    } else if (!dialog.hasAttribute('aria-labelledby')) {
      setAttribute(dialog, 'aria-label', 'Quick view');
    }

    const close = dialog.querySelector(
      '.gfqv-close-modal, .yv_side_drawer_close, [data-drawer-close]'
    );
    if (close) {
      if (close.tagName !== 'BUTTON') {
        setAttribute(close, 'role', 'button');
        setAttribute(close, 'tabindex', '0');
      }
      setAttribute(close, 'aria-label', 'Close quick view');
    }

    if (!isGlobo) {
      const quantity = dialog.querySelector('.yv-product-quantity');
      const productButton = dialog.querySelector('.product_button');
      const productForm = productButton?.querySelector(':scope > form.main-product-form');
      const productAction = productForm?.querySelector(':scope > .pdp-action-container');
      if (quantity && productForm && productAction && quantity.nextElementSibling !== productAction) {
        productForm.insertBefore(quantity, productAction);
      }
      enhanceNativeProductGroup(dialog);
      addPaymentIcons(dialog, productButton, '.uv-quickview-meta');
      return;
    }

    const variantSelect = dialog.querySelector('#gfqv-select');
    const productDataNode = dialog.querySelector('#ProductJson-product-template');
    const cartButtonWrap = dialog.querySelector('#gfqv-btn-wrap');
    let productData = null;
    if (productDataNode) {
      try {
        productData = JSON.parse(productDataNode.textContent);
      } catch (_error) {
        productData = null;
      }
    }

    const quantity = dialog.querySelector('.variations_button .quantity');
    const quantityInput = quantity?.querySelector('input[name="quantity"]');
    const quantityHost = quantity?.parentElement;
    if (quantity && quantityInput && quantityHost) {
      const quantityId = `gfqv-quantity-${productData?.id || 'product'}`;
      let quantityLabel = dialog.querySelector('.uv-gfqv-quantity-label');
      if (!quantityLabel) {
        quantityLabel = document.createElement('label');
        quantityLabel.className = 'uv-gfqv-quantity-label';
      }
      if (quantity.previousElementSibling !== quantityLabel) {
        quantity.insertAdjacentElement('beforebegin', quantityLabel);
      }
      setAttribute(quantityInput, 'id', quantityId);
      setAttribute(quantityLabel, 'for', quantityId);
      if (quantityLabel.textContent !== quantityLabelText) {
        quantityLabel.textContent = quantityLabelText;
      }
    }

    const swatches = [...dialog.querySelectorAll('.gfqv-swatch')];
    const findSwatch = (name) => swatches.find((swatch) => {
      const label = swatch.querySelector('label, .gfqv-swatch-label');
      return label?.textContent.trim().toLowerCase().includes(name);
    });
    const colorSwatch = findSwatch('color');
    const sizeSwatch = findSwatch('size');
    colorSwatch?.classList.add('uv-gfqv-color-swatch');
    sizeSwatch?.classList.add('uv-gfqv-size-swatch');

    colorSwatch
      ?.querySelectorAll(':scope > .gfqv-swatch-values > .gfqv-swatch-value')
      .forEach((value) => {
        const face = value.querySelector(
          '.gfqv-swatch-color, [style*="background-color"], [style*="background-image"]'
        );
        if (!face) return;
        value.classList.add('uv-gfqv-native-color-value');
        face.classList.add('uv-gfqv-native-color-face');
      });

    const priceWrapper = dialog.querySelector('.gfqv-product-card__price-wrapper');
    let shipping = dialog.querySelector('.uv-gfqv-shipping');
    if (priceWrapper && !shipping) {
      shipping = document.createElement('p');
      shipping.className = 'uv-gfqv-shipping';
      const shippingLink = document.createElement('a');
      shippingLink.href = '/policies/shipping-policy';
      shippingLink.textContent = 'Free shipping';
      shipping.append(shippingLink, ' (all incl.)');
    }
    if (priceWrapper && shipping && priceWrapper.nextElementSibling !== shipping) {
      priceWrapper.insertAdjacentElement('afterend', shipping);
    }

    const quickViewProductId = productData?.id ? String(productData.id) : '';
    const triggerCard = quickViewTrigger?.closest(
      '.spf-product-card[product-selector]'
    );
    const sourceCard =
      String(triggerCard?.getAttribute('product-selector') || '') === quickViewProductId
        ? triggerCard
        : quickViewProductId
          ? document.querySelector(
            `.spf-product-card[product-selector="${productData.id}"]`
          )
          : null;
    syncGloboProductColorGroup({
      colorSwatch,
      productId: quickViewProductId,
      sourceGroup: sourceCard?.querySelector('.gsw-list-product-groups') || null
    });

    if (variantSelect && productData?.variants?.length && cartButtonWrap) {

      let meta = dialog.querySelector('.uv-gfqv-meta');
      if (!meta) {
        meta = document.createElement('div');
        meta.className = 'uv-gfqv-meta';
        meta.innerHTML = '<span>SKU:</span><span data-product-sku></span>';
        cartButtonWrap.insertAdjacentElement('afterend', meta);
      }

      const updateSku = () => {
        const liveVariantSelect = dialog.querySelector('#gfqv-select');
        const liveProductDataNode = dialog.querySelector('#ProductJson-product-template');
        const liveMeta = dialog.querySelector('.uv-gfqv-meta');
        if (!liveVariantSelect || !liveProductDataNode || !liveMeta) return;

        let liveProductData = null;
        try {
          liveProductData = JSON.parse(liveProductDataNode.textContent);
        } catch (_error) {
          liveProductData = null;
        }

        const variant = liveProductData?.variants?.find(
          (item) => String(item.id) === String(liveVariantSelect.value)
        );
        const value = variant?.sku?.trim?.() || '';
        const sku = liveMeta.querySelector('[data-product-sku]');
        if (sku && sku.textContent !== value) sku.textContent = value;
        if (liveMeta.hidden !== !value) liveMeta.hidden = !value;
      };

      if (variantSelect.dataset.uvSkuReady !== 'true') {
        variantSelect.addEventListener('change', updateSku);
        variantSelect.dataset.uvSkuReady = 'true';
      }
      updateSku();
    }

    addPaymentIcons(dialog, cartButtonWrap, '.uv-gfqv-meta');

    dialog.querySelectorAll('.gfqv-description > p').forEach((paragraph) => {
      const hasMeaningfulMedia = paragraph.querySelector(
        'img, picture, video, iframe, svg, table, ul, ol, blockquote'
      );
      if (!paragraph.textContent.trim() && !hasMeaningfulMedia) {
        paragraph.hidden = true;
        setAttribute(paragraph, 'aria-hidden', 'true');
      }
    });

    dialog.querySelectorAll('.owl-prev, .owl-next').forEach((control) => {
      setAttribute(control, 'role', 'button');
      setAttribute(control, 'tabindex', '0');
      setAttribute(
        control,
        'aria-label',
        control.classList.contains('owl-prev') ? 'Previous product image' : 'Next product image'
      );
    });

    dialog.querySelectorAll('.quantity .decrease, .quantity .increase').forEach((control) => {
      setAttribute(control, 'role', 'button');
      setAttribute(control, 'tabindex', '0');
      setAttribute(
        control,
        'aria-label',
        control.classList.contains('decrease') ? 'Decrease quantity' : 'Increase quantity'
      );
    });

    dialog
      .querySelectorAll('.gfqv-swatch-value:not(.uv-gfqv-variant-sentinel)')
      .forEach((control) => {
        setAttribute(control, 'role', 'button');
        setAttribute(control, 'tabindex', '0');
        const value = control.getAttribute('data-value');
        if (value) setAttribute(control, 'aria-label', `Select ${value}`);
      });
  };

  const enhancePickupDrawer = (drawer, open) => {
    if (!drawer) return;
    setAttribute(drawer, 'role', 'dialog');
    setAttribute(drawer, 'aria-modal', 'true');
    setAttribute(drawer, 'aria-hidden', !open);
    if (!drawer.hasAttribute('tabindex')) setAttribute(drawer, 'tabindex', '-1');

    const heading = drawer.querySelector('[data-drawer-title]');
    if (heading) {
      if (!heading.id) heading.id = 'pickup-availability-title';
      setAttribute(drawer, 'aria-labelledby', heading.id);
      drawer.removeAttribute('aria-label');
    } else if (!drawer.hasAttribute('aria-labelledby')) {
      setAttribute(drawer, 'aria-label', 'Pickup availability');
    }

    const close = drawer.querySelector(
      '.yv_side_drawer_close, [data-drawer-close]'
    );
    if (close) {
      setAttribute(close, 'aria-label', 'Close pickup availability');
      if (close.tagName === 'BUTTON') setAttribute(close, 'type', 'button');
    }
  };

  const quickViewReturnTarget = (trigger) => {
    if (!trigger?.isConnected) return null;
    if (!trigger.matches('.open-quick-view') || isVisible(trigger)) return trigger;
    const productLink = trigger.closest('.spf-product-card')
      ?.querySelector('.spf-product-card__image-wrapper[href]');
    return isVisible(productLink) ? productLink : null;
  };

  const syncQuickView = () => {
    const globoDialog = document.getElementById('gfqv-modal');
    const nativeDialog = document.getElementById('yv_quickView_product');
    const cartDrawer = document.getElementById('mini__cart');
    const cartOwnsOpenSharedSurface = Boolean(
      cartDrawer &&
        cartDrawer.classList.contains('mini_cart') &&
        (document.body.classList.contains('side_Drawer_open') ||
          document.body.classList.contains('minicart-open'))
    );
    const pickupOwnsOpenSharedSurface = pickupDrawerIsOpen();
    if (globoDialog) enhanceQuickView(globoDialog);
    if (nativeDialog?.classList.contains('yv_quickView_product')) {
      enhanceQuickView(nativeDialog);
    }

    const open = quickViewIsOpen();
    if (open && !quickViewWasOpen) {
      window.setTimeout(() => {
        const dialog = quickViewDialog();
        focusForCurrentModality(
          dialog?.querySelector(
            '.gfqv-close-modal, .yv_side_drawer_close, [data-drawer-close]'
          )
        );
      }, 80);
    } else if (
      !open &&
      quickViewWasOpen &&
      quickViewTrigger?.isConnected &&
      !cartOwnsOpenSharedSurface &&
      !pickupOwnsOpenSharedSurface
    ) {
      const returnTarget = quickViewReturnTarget(quickViewTrigger);
      if (returnTarget) focusForCurrentModality(returnTarget);
    }

    quickViewWasOpen = open;
    syncBackgroundInert();
  };

  const closeQuickView = () => {
    quickViewDialog()
      ?.querySelector('.gfqv-close-modal, .yv_side_drawer_close, [data-drawer-close]')
      ?.click();
  };

  const syncCartDrawer = () => {
    const drawer = document.getElementById('mini__cart');
    if (!drawer || !drawer.classList.contains('mini_cart')) {
      consumeCartTrigger();
      cartWasOpen = false;
      syncBackgroundInert();
      return;
    }
    const open = cartDrawerIsOpen();
    setAttribute(drawer, 'role', 'dialog');
    setAttribute(drawer, 'aria-modal', 'true');
    setAttribute(drawer, 'aria-hidden', !open);

    const heading = drawer.querySelector('.mini-cart-title, .cart-drawer-title, h2, h3');
    if (heading) {
      if (!heading.id) heading.id = 'mini-cart-title';
      setAttribute(drawer, 'aria-labelledby', heading.id);
    } else if (!drawer.hasAttribute('aria-label')) {
      setAttribute(drawer, 'aria-label', 'Shopping cart');
    }

    const close = drawer.querySelector('.yv_side_drawer_close, .close-cart-drawer');
    if (close) {
      setAttribute(close, 'aria-label', 'Close cart');
      if (close.tagName === 'BUTTON') setAttribute(close, 'type', 'button');
    }

    if (open && !cartWasOpen) {
      window.setTimeout(() => {
        if (cartDrawerIsOpen()) {
          focusForCurrentModality(
            drawer.querySelector('.yv_side_drawer_close, .close-cart-drawer') ||
              visibleFocusable(drawer)[0] ||
              drawer
          );
        }
      }, 60);
    } else if (!open && cartWasOpen) {
      const returnTarget = consumeCartTrigger();
      if (returnTarget?.isConnected) {
        focusForCurrentModality(returnTarget);
      }
    }
    cartWasOpen = open;
    syncBackgroundInert();
  };

  const syncPickupDrawer = () => {
    const drawer = pickupDrawer();
    const open = pickupDrawerIsOpen();
    const ownership = open ? currentPickupOwnership(drawer) : null;
    const ownershipChanged = open && ownership !== pickupOwnership;

    if (!open && (pickupWasOpen || pickupTrigger || pickupOwnership)) {
      pickupFocusGeneration += 1;
      const ownerReturnTarget = pickupOwnership?.returnFocusTarget;
      const returnTarget = pickupOwnership ?
        ownerReturnTarget?.isConnected ? ownerReturnTarget : null :
        pickupTrigger?.isConnected ? pickupTrigger : null;
      const successorOwnsSharedDrawer =
        nativeQuickViewIsOpen() || cartDrawerIsOpen();
      pickupTrigger = null;
      pickupWasOpen = false;
      pickupOwnership = null;

      // Release the background before attempting to return focus. Otherwise
      // Chromium rejects focus on the inert opener and may also reject hiding
      // a drawer that still contains the active element.
      syncBackgroundInert();
      if (!successorOwnsSharedDrawer) {
        let focusTarget = returnTarget;
        if (!focusTarget && drawer?.contains?.(document.activeElement)) {
          focusTarget = document.getElementById('MainContent');
          if (focusTarget && !focusTarget.hasAttribute('tabindex')) {
            setAttribute(focusTarget, 'tabindex', '-1');
          }
        }
        if (
          focusTarget?.isConnected &&
          document.activeElement !== focusTarget
        ) {
          focusForCurrentModality(focusTarget);
        }
        if (drawer?.contains?.(document.activeElement)) {
          const mainContent = document.getElementById('MainContent');
          if (mainContent && !mainContent.hasAttribute('tabindex')) {
            setAttribute(mainContent, 'tabindex', '-1');
          }
          if (
            mainContent?.isConnected &&
            document.activeElement !== mainContent
          ) {
            focusForCurrentModality(mainContent);
          }
        }
      }

      if (drawer && !drawer.contains?.(document.activeElement)) {
        enhancePickupDrawer(drawer, false);
      }
      return;
    }

    if (drawer && (open || !drawer.contains?.(document.activeElement))) {
      enhancePickupDrawer(drawer, open);
    }

    if (open && (!pickupWasOpen || ownershipChanged)) {
      const focusGeneration = ++pickupFocusGeneration;
      const focusOwnership = ownership;
      const focusDrawer = drawer;
      pickupOwnership = ownership;
      window.setTimeout(() => {
        if (
          focusGeneration !== pickupFocusGeneration ||
          !pickupDrawerIsOpen()
        ) return;
        const currentDrawer = pickupDrawer();
        const activeOwnership = currentPickupOwnership(currentDrawer);
        if (
          !currentDrawer ||
          currentDrawer !== focusDrawer ||
          activeOwnership !== focusOwnership
        ) return;
        const initialFocus =
          currentDrawer.querySelector(
            '.yv_side_drawer_close, [data-drawer-close]'
          ) || visibleFocusable(currentDrawer)[0] || currentDrawer;
        if (!currentDrawer.contains(document.activeElement)) {
          focusForCurrentModality(initialFocus);
        }
      }, 60);
    } else if (!open) {
      pickupOwnership = null;
    }

    pickupWasOpen = open;
    syncBackgroundInert();
  };

  const syncProductRailArrowAxis = (root = document) => {
    const sliderSelector = '.yv-collection-product-grid.product-grid-carousel';
    const sliders = new Set();
    if (root.matches?.(sliderSelector)) sliders.add(root);
    root.querySelectorAll?.(sliderSelector).forEach((slider) => sliders.add(slider));

    if ('ResizeObserver' in window && !productRailResizeObserver) {
      productRailResizeObserver = new ResizeObserver(() => queueMerchandisingSync());
    }

    sliders.forEach((slider) => {
      const image = slider.querySelector('.yv-product-card.is-selected .yv-product-img')
        || slider.querySelector('.yv-product-card .yv-product-img');
      if (!image) return;

      const sliderRect = slider.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      if (!imageRect.height) return;

      const imageCenter = imageRect.top - sliderRect.top + imageRect.height / 2;
      const axisValue = `${imageCenter.toFixed(2)}px`;
      if (
        slider.style.getPropertyValue('--uv-rail-arrow-y') !== axisValue ||
        slider.style.getPropertyPriority('--uv-rail-arrow-y') !== ''
      ) {
        slider.style.setProperty('--uv-rail-arrow-y', axisValue);
      }
      if (
        slider.classList.contains('flickity-enabled') &&
        slider.querySelector(':scope > .flickity-prev-next-button')
      ) {
        if (slider.hasAttribute('data-uv-rail-axis-fallback')) {
          slider.removeAttribute('data-uv-rail-axis-fallback');
        }
        setAttribute(slider, 'data-uv-rail-axis-ready', 'true');
      }
      productRailResizeObserver?.observe(slider);
      productRailResizeObserver?.observe(image);
    });
  };

  const releaseUnmeasuredProductRailArrows = () => {
    document
      .querySelectorAll('.yv-collection-product-grid.product-grid-carousel.flickity-enabled')
      .forEach((slider) => {
        if (!slider.closest(productRailSectionSelector)) return;
        if (!slider.hasAttribute('data-uv-rail-axis-ready')) {
          setAttribute(slider, 'data-uv-rail-axis-fallback', 'true');
        }
      });
  };

  function queueOverlaySync() {
    if (overlaySyncQueued) return;
    overlaySyncQueued = true;
    window.requestAnimationFrame(() => {
      overlaySyncQueued = false;
      syncSearchLayers();
      syncCatalog();
      syncMenu();
      syncGloboCollectionSearch();
      syncGloboCollectionZeroState();
      syncGloboFilterRecovery();
      syncFilter();
      syncQuickView();
      syncCartDrawer();
      syncPickupDrawer();
    });
  }

  function queueMerchandisingSync(root = document) {
    if (root === document) {
      pendingMerchandisingRoots.clear();
      pendingMerchandisingRoots.add(document);
    } else if (!pendingMerchandisingRoots.has(document) && root?.querySelectorAll) {
      pendingMerchandisingRoots.add(root);
    }

    if (merchandisingSyncQueued) return;
    merchandisingSyncQueued = true;
    window.requestAnimationFrame(() => {
      merchandisingSyncQueued = false;
      const roots = [...pendingMerchandisingRoots];
      pendingMerchandisingRoots.clear();
      roots.forEach((syncRoot) => {
        if (syncRoot !== document && !syncRoot.isConnected) return;
        enhanceTopPicksProductGroups(syncRoot);
        syncProductRailArrowAxis(syncRoot);
      });
    });
  }

  /*
   * Flickity, responsive images and Globo groups can each finish after the
   * first paint. A short bounded settle cycle keeps the media axis exact
   * without observing style mutations or creating a feedback loop.
   */
  const settleMerchandising = (root = document) => {
    queueMerchandisingSync(root);
    [240, 1000, 2500].forEach((delay) => {
      window.setTimeout(() => queueMerchandisingSync(root), delay);
    });
    document.fonts?.ready.then(() => queueMerchandisingSync(root));
  };

  function queueRuntimeSync(root = document) {
    queueOverlaySync();
    queueMerchandisingSync(root);
  }

  const matchingElements = (root, selector) => {
    const matches = new Set();
    if (root?.matches?.(selector)) matches.add(root);
    root?.querySelectorAll?.(selector).forEach((element) => matches.add(element));
    return matches;
  };

  const registerRuntimeRoots = (root = document) => {
    const roots = matchingElements(root, runtimeRootSelector);
    roots.forEach((runtimeRoot) => {
      if (observedRuntimeRoots.has(runtimeRoot)) return;
      runtimeStateObserver?.observe(runtimeRoot, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden']
      });
      observedRuntimeRoots.add(runtimeRoot);
    });
    return roots.size > 0;
  };

  const registerMerchandisingSections = (root = document) => {
    const sections = matchingElements(root, productRailSectionSelector);
    sections.forEach((section) => {
      if (observedMerchandisingSections.has(section)) return;
      merchandisingObserver?.observe(section, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'aria-hidden']
      });
      observedMerchandisingSections.add(section);
    });
    return sections;
  };

  const initRuntimeObservers = () => {
    runtimeStateObserver = new MutationObserver(queueOverlaySync);
    merchandisingObserver = new MutationObserver((mutations) => {
      const sections = new Set();
      mutations.forEach((mutation) => {
        const section = mutation.target.closest?.(productRailSectionSelector);
        if (section) sections.add(section);
      });
      sections.forEach((section) => queueMerchandisingSync(section));
    });

    bodyStateObserver = new MutationObserver(queueOverlaySync);
    bodyStateObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    documentStateObserver = new MutationObserver(queueOverlaySync);
    documentStateObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    runtimeDiscoveryObserver = new MutationObserver((mutations) => {
      let runtimeChanged = false;
      mutations.forEach((mutation) => {
        if (mutation.target.closest?.(runtimeRootSelector)) runtimeChanged = true;

        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (registerRuntimeRoots(node)) runtimeChanged = true;
          registerMerchandisingSections(node).forEach((section) => {
            queueMerchandisingSync(section);
          });
        });

        mutation.removedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches(runtimeRootSelector) || node.querySelector(runtimeRootSelector)) {
            runtimeChanged = true;
          }
        });
      });
      if (runtimeChanged) queueOverlaySync();
    });
    runtimeDiscoveryObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    registerRuntimeRoots();
    registerMerchandisingSections();
  };

  const init = () => {
    initGloboCollectionSearch();
    syncFaq();
    enhanceCatalog();
    /* Pre-measure the media axis before Flickity inserts visible controls. */
    syncProductRailArrowAxis();
    enhanceTopPicksProductGroups();
    /* Close the initial keyboard-focus gap before the first animation frame. */
    syncMenu();
    initRuntimeObservers();
    queueRuntimeSync();
    window.setTimeout(releaseUnmeasuredProductRailArrows, 1600);

    window.addEventListener('Globo.product.groups.loaded', () => {
      enhanceTopPicksProductGroups();
      queueOverlaySync();
      settleMerchandising();
    });

    document.addEventListener('pointerdown', (event) => {
      const control = pointerFocusControl(event.target);
      setPointerFocusedRail(event.target);
      setFocusModality('pointer', control);
    }, true);

    /*
     * Some embedded browsers expose a real mouse activation as `click`
     * without forwarding `pointerdown`. `detail > 0` keeps this fallback
     * pointer-only, so keyboard-triggered clicks retain their focus ring.
     */
    document.addEventListener('click', (event) => {
      if (event.detail <= 0) return;
      const control = pointerFocusControl(event.target);
      const rail = pointerFocusRail(event.target);
      if (!control && !rail) return;
      setPointerFocusedRail(event.target);
      setFocusModality('pointer', control);
    }, true);

    document.addEventListener('keydown', (event) => {
      if (!['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) {
        setFocusModality('keyboard');
      }
    }, true);

    window.addEventListener('resize', () => queueRuntimeSync(), { passive: true });

    if (document.readyState === 'complete') {
      settleMerchandising();
    } else {
      window.addEventListener('load', () => settleMerchandising(), { once: true });
    }

    document.addEventListener('click', (event) => {
      const headerSearchInput = event.target.closest('#search-drawer-input-query');
      const searchButton = event.target.closest(
        'a.search-form, .header-search-group [type="search"], #search-drawer-input-query'
      );
      captureSearchTrigger(searchButton, headerSearchInput);
      if (searchButton && window.matchMedia('(max-width: 767px)').matches) {
        document.body.classList.remove('addsearch');
        const nativeSearch = document.querySelector('.search-bar-container');
        if (nativeSearch) setAttribute(nativeSearch, 'aria-hidden', 'true');
      }

      const catalogButton = event.target.closest('#catalog-view');
      if (catalogButton) {
        window.setTimeout(syncCatalog, 0);
      } else if (catalogWasOpen && !event.target.closest('.yv-browse-category')) {
        closeCatalog(false);
      }

      const menuButton = event.target.closest('.navbar-toggler');
      if (menuButton) menuTrigger = menuButton;

      const filterButton = event.target.closest(
        '.gf-refine-toggle-mobile button, .gf-filter-trigger button, [aria-label="Filter By"]'
      );
      if (filterButton) {
        filterTrigger = filterButton;
        document.getElementById('gf-tree')?.classList.remove('uv-force-closed');
      }

      const quickButton = event.target.closest('.open-quick-view, .quickView');
      if (quickButton) quickViewTrigger = quickButton;

      const pickupButton = event.target.closest('#ShowPickupAvailabilityDrawer');
      if (pickupButton) {
        const sharedDrawer = document.querySelector('[data-side-drawer]');
        const sharedOwner = window.UpvanityVariantLifecycle?.sharedDrawerOwners?.get?.(
          sharedDrawer
        );
        const ownerReturnTarget =
          sharedOwner?.owner === 'pickup' ? sharedOwner.returnFocusTarget : null;
        pickupTrigger = ownerReturnTarget?.isConnected ?
          ownerReturnTarget :
          pickupButton.isConnected ? pickupButton : null;
      }

      const filterClose = event.target.closest('#gf-tree .gf-close-canvas');
      if (filterClose) {
        window.setTimeout(() => {
          const dialog = document.getElementById('gf-tree');
          if (dialog && dialog.getAttribute('aria-hidden') !== 'true') forceFilterClosed();
        }, 160);
      } else if (filterWasOpen && !event.target.closest('#gf-tree') && !filterButton) {
        closeFilter();
      }

      if (event.target.closest('.faqSection-header, .yv-accordion-header')) {
        window.setTimeout(syncFaq, 0);
        window.setTimeout(syncFaq, 450);
      }

      if (event.target.closest('#mobileMenu button.toggle, #mobileMenu button.toggle-level')) {
        window.setTimeout(syncSubmenus, 0);
        window.setTimeout(syncSubmenus, 500);
      }

      queueOverlaySync();
    });

    document.addEventListener('focusin', (event) => {
      if (document.documentElement.dataset.uvFocusModality === 'pointer') {
        const control = pointerFocusControl(event.target);
        const rail = pointerFocusRail(event.target);
        const pointerOrigin = Boolean(control || (rail && rail === pointerFocusedRail));
        setFocusModality(pointerOrigin ? 'pointer' : 'keyboard', control);
      }

      const filterDialog = filterIsOpen() ? document.getElementById('gf-tree') : null;
      if (filterDialog && !filterDialog.contains(event.target)) {
        event.stopPropagation();
        (filterDialog.querySelector('.gf-close-canvas') || visibleFocusable(filterDialog)[0])?.focus();
      }
    }, true);

    document.addEventListener('keydown', (event) => {
      const menu = document.getElementById('mobileMenu');
      const activeSearchLayer = searchIsOpen()
        ? document.getElementById('glFilter-search-popup')
        : null;
      const filterDialog = filterIsOpen() ? document.getElementById('gf-tree') : null;
      const activeQuickViewDialog = quickViewDialog();
      const cartDrawer = cartDrawerIsOpen() ? document.getElementById('mini__cart') : null;
      const activePickupDrawer = pickupDrawerIsOpen() ? pickupDrawer() : null;

      const syntheticQuickViewButton = event.target.closest?.(
        '#gfqv-modal [role="button"], #yv_quickView_product [role="button"]'
      );

      const catalogButton = event.target.closest?.('#catalog-view');
      if (catalogButton && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        catalogButton.click();
        return;
      }
      if (
        syntheticQuickViewButton &&
        syntheticQuickViewButton.tagName !== 'BUTTON' &&
        syntheticQuickViewButton.tagName !== 'A' &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        syntheticQuickViewButton.click();
        return;
      }

      if (event.key === 'Escape' && activeSearchLayer) {
        event.preventDefault();
        activeSearchLayer.querySelector('.gl-d-close-btn')?.click();
        window.setTimeout(queueOverlaySync, 0);
        return;
      }

      if (event.key === 'Escape' && activeQuickViewDialog) {
        event.preventDefault();
        closeQuickView();
        return;
      }

      if (event.key === 'Escape' && activePickupDrawer) {
        event.preventDefault();
        activePickupDrawer
          .querySelector('.yv_side_drawer_close, [data-drawer-close]')
          ?.click();
        return;
      }

      if (event.key === 'Escape' && catalogWasOpen) {
        event.preventDefault();
        closeCatalog(true);
        return;
      }

      if (event.key === 'Escape' && filterDialog) {
        event.preventDefault();
        closeFilter();
        return;
      }

      if (event.key === 'Escape' && menuWasOpen && menu) {
        event.preventDefault();
        menu.querySelector('.close-btn')?.click();
        return;
      }

      if (event.key === 'Escape' && cartDrawer) {
        event.preventDefault();
        cartDrawer.querySelector('.yv_side_drawer_close, .close-cart-drawer')?.click();
        return;
      }

      if (event.key === 'Tab' && activeQuickViewDialog) {
        trapTab(event, activeQuickViewDialog);
      } else if (event.key === 'Tab' && activePickupDrawer) {
        trapTab(event, activePickupDrawer);
      } else if (event.key === 'Tab' && filterDialog) {
        trapTab(event, filterDialog);
      } else if (event.key === 'Tab' && menuWasOpen && menu) {
        trapTab(event, menu);
      } else if (event.key === 'Tab' && cartDrawer) {
        trapTab(event, cartDrawer);
      }
    }, true);

    document.addEventListener('shopify:section:load', (event) => {
      syncFaq(event.target);
      registerRuntimeRoots(event.target);
      registerMerchandisingSections(event.target);
      queueRuntimeSync(event.target);
      settleMerchandising(event.target);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
